import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import BreadCrumbs from "@/components/Breadcrumbs";
import Tabs from "@/components/VerificationChecks/Tabs";
import AccountSidebarNav from "@/components/AccountSidbarNav";
import { AppDispatch, AppState } from "@/store";
import {
  createBCRequestByJobseekerIds,
  getPackgageById,
  sendInviteForBcRequest,
} from "@/api/backgroundCheck";
import { getPackagesAction, setSelectedPackage } from "@/store/slices/packageSlice";
import { useParams } from "react-router-dom";
import { ShieldCheck, ShoppingCart, User } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMixpanel } from "@/hooks/useMixpanel";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import { searchUserByEmail } from "@/api/user";
import { send } from "vite";
import { validateAndSuggestEmailDomain } from "@/utils/validation";
import EmailDomainPopup from "@/components/auth/EmailDomainPopup";

const sidebarNavItems = [
  {
    title: t("verification_checks_pricing.sidebar.verification_checks_pricing"),
    href: "/employer/verification-checks/pricing",
  },
  {
    title: t("verification_checks_pricing.sidebar.background_checks_dashboard"),
    href: "/employer/verification-checks/dashboard",
  },
];
const isValidEmail = (email: string) =>  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

export default function SendRequestWithoutJobPostPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { trackEvent } = useMixpanel();
  const { allPackages, selectedPackage } = useSelector((state: AppState) => state.packages);
  const [packageDetails, setPackageDetails] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const [candidates, setCandidates] = useState<any>("");
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [suggestedDomain, setSuggestedDomain] = useState<string>("");
  const [showDomainPopup, setShowDomainPopup] = useState<boolean>(false);
  const [isChatLoading, setIsChatLoading] = useState<boolean>(false);

  const validateEmailDomain = (email: string) => {
    const { isValid, suggestedDomain: suggestion } = validateAndSuggestEmailDomain(email);
    if (isValid) {
      setShowDomainPopup(false);
      setSuggestedDomain("");
    } else if (suggestion) {
      setSuggestedDomain(suggestion);
      setShowDomainPopup(true);
    } else {
      setShowDomainPopup(false);
      setSuggestedDomain("");
    }
  };

  const handleCandidateSelect = (id: string) => {
    if (selectedCandidates.includes(id)) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates([id]);
    }
    trackEvent("Candidate Selection Toggled", { candidateId: id });
  };
  const handleProceed = async () => {
    if (!selectedCandidates.length) {
      toast({ title: "Please select at least one candidate." });
      return;
    }

    trackEvent("Proceed Button Clicked", { selectedCandidatesCount: selectedCandidates.length });

    const payload = {
      jobseeker_ids: selectedCandidates,
      pkg_id: selectedPackage?.pkg_id || packageDetails?.pkg_id,
    };
    try {
      const response = await createBCRequestByJobseekerIds(payload);
      if (response) {
        toast({ title: "Request created successfully." });
        setSelectedCandidates([]);
        setCandidates("");
        setSearchQuery("");
        trackEvent("Request Created Successfully", { payload });
      } else {
        toast({ title: "Failed to create the request." });
        trackEvent("Request Creation Failed", { payload });
      }
    } catch (error) {
      console.log("error", error);
      toast({ title: error?.response?.data?.error?.message || "An unexpected error occurred." });
      trackEvent("Request Exception", { payload, error: error?.message });
    }
  };

  const handleInvite = async () => {
    if (!isValidEmail(emailValue.trim())) {
      toast({ title: "Please enter a valid email address." });
      return;
    }

    trackEvent("Invite Button Clicked", { email: emailValue });

    const payload = {
      emails: [emailValue.trim()],
      pkg_id: selectedPackage?.pkg_id || packageDetails?.pkg_id,
    };

    try {
      const response = await sendInviteForBcRequest(payload);

      if (response?.success) {
        toast({ title: "Invite sent successfully." });
        setEmailValue("");
        setSearchQuery("");
        setCandidates("");
        trackEvent("Invite Sent Successfully", { payload });
      } else {
        const errorMessage = response?.error?.message || "Failed to send invite.";
        toast({ title: errorMessage });
        trackEvent("Invite Failed", { payload, error: errorMessage });
      }
    } catch (error) {
      if (error?.response?.data?.error?.message === "Jobseekers already exist!") {
        toast({ title: "Jobseeker already exist!" });
      } else {
        console.log("error", error);
        toast({ title: error?.response?.data?.error?.message || "An unexpected error occurred." });
      }
      trackEvent("Invite Exception", { payload, error: error?.message });
    }
  };
  const handleSearch = async () => {
    if (!isValidEmail(searchQuery.trim())) {
      toast({ title: "Please enter a valid email address." });
      return;
    }
    trackEvent("Search Button Clicked", { query: searchQuery.trim() });
    validateEmailDomain(searchQuery.trim());
    // Manually refetch query
    setIsChatLoading(true);
    try {
      const data = await searchUserByEmail(searchQuery.trim());
      const user = data.result;
      if (user && user.type === "job_seeker") {
        setCandidates(user);
        setIsChatLoading(false);
      } else {
        setCandidates(null);
        setIsChatLoading(false);
      }
      return data;
    } catch (err) {
      setCandidates(null);
      setEmailValue(searchQuery.trim());
      setIsChatLoading(false);
      return null;
    }
  };
  const fetchPackageDetails = async () => {
    try {
      const response = await getPackgageById(id!);
      setPackageDetails(response.result);
      dispatch(setSelectedPackage(response.result));
      trackEvent("Package Details Fetched", { packageId: id });
    } catch (error) {
      toast({ title: "Error fetching package details" });
      trackEvent("Package Details Fetch Error", { packageId: id });
    }
  };
  const handleConfirmEmailDomain = (domain: string) => {
    setShowDomainPopup(false);
    setSuggestedDomain("");
    const email = searchQuery.split("@")[0];
    setSearchQuery(`${email}@${domain}`);
  };
  useEffect(() => {
    fetchPackageDetails();
  }, [id]);

  useEffect(() => {
    if (!allPackages.length) {
      dispatch(getPackagesAction("employer"));
      trackEvent("Packages Fetch Initiated");
    }
  }, [dispatch, allPackages.length]);

  return (
    <>
      <BreadCrumbs
        links={[
          { title: t("verification_checks_pricing.breadcrumbs.verification_checks"), to: "#" },
          {
            title: t("jobseeker_bc_application_form.sidebar.verification_checks_pricing"),
            to: "employer/verification-checks/dashboard",
          },
        ]}
      />

      <div className="mb-6 flex flex-col">
        <aside className="hidden md:block lg:w-full">
          <Tabs tabs={sidebarNavItems} />
        </aside>
        <aside className="block md:hidden">
          <AccountSidebarNav items={sidebarNavItems} />
        </aside>
      </div>

      <div className="mb-96 w-full md:mb-0">
        <h1 className="mb-4 text-xl font-semibold">
          {t("apply_request_dialog.title")} : {t("send_request_without_job_post.title")}
        </h1>
        <p className="text-lg font-semibold text-secondary">
          {selectedPackage?.name ?? packageDetails?.name} - $
          {selectedPackage?.price || packageDetails?.price}
        </p>
        <p className="mb-6 w-full text-muted-foreground md:w-[90%]">
          {t("send_request_without_job_post.instructions.select_candidates")}
        </p>

        <div className="mb-4 flex gap-2 md:mb-6 md:w-[90%]">
          <Input
            type="email"
            placeholder={t("send_request_without_job_post.candidate.search_placeholder")}
            value={searchQuery}
            className="flex-1"
            onChange={(e: any) => {
              setSearchQuery(e.target.value);
              validateEmailDomain(e.target.value.trim());
              trackEvent("Search Query Changed", { query: e.target.value });
            }}
          />
          <Button variant="default" size="lg" onClick={handleSearch}>
            Search
          </Button>
        </div>
        <p className="-mt-2 ml-2 text-xs text-red-500 md:-mt-4">
          {!isValidEmail(searchQuery.trim()) &&
            searchQuery.length > 4 &&
            "Please enter a valid email address"}
        </p>
        {showDomainPopup && (
          <EmailDomainPopup
            domain={suggestedDomain}
            onCancel={() => setShowDomainPopup(false)}
            onConfirm={handleConfirmEmailDomain}
          />
        )}
        <div className="mt-6 flex w-full justify-between gap-10 md:mt-10 md:w-[90%]">
          <div className="mb-20 grid w-full gap-4 overflow-auto md:w-1/2">
            {isChatLoading ? (
              //  Show loading skeleton while searching
              Array.from({ length: 3 }).map((_, index) => (
                <Card
                  key={index}
                  className="flex items-center justify-between border p-4 shadow-sm"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-5 w-5 animate-pulse rounded bg-gray-300"></div>
                    <div className="flex items-center gap-2">
                      <div className="h-10 w-10 animate-pulse rounded-full bg-gray-300"></div>
                      <div>
                        <div className="mb-2 h-4 w-24 animate-pulse bg-gray-300"></div>
                        <div className="h-4 w-32 animate-pulse bg-gray-300"></div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 h-4 w-16 animate-pulse bg-gray-300"></div>
                    <div className="h-4 w-20 animate-pulse bg-gray-300"></div>
                  </div>
                </Card>
              ))
            ) : candidates === "" ? (
              //  Initial blank state (no search yet)
              <></>
            ) : candidates?.email ? (
              //  Show result if user is found
              <Card className="flex items-center justify-between border-2 border-muted p-4 shadow-sm hover:border-primary">
                <div className="flex items-center gap-4">
                  <Checkbox
                    checked={selectedCandidates.includes(candidates?.id)}
                    onCheckedChange={() => handleCandidateSelect(candidates?.id)}
                  />
                  <div className="flex items-center gap-2">
                    <Avatar>
                      <AvatarImage
                        src={candidates?.jobseeker?.photo_thumbnail_url}
                        alt={candidates?.full_name}
                      />
                      <AvatarFallback>
                        <User className="h-full w-full rounded-full bg-secondary p-2 text-white" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-md flex items-center font-semibold">
                        {candidates.full_name}
                        {candidates?.bc_verified && (
                          <div className="flex items-center justify-center text-primary">
                            <ShieldCheck className="h-5 w-5 cursor-pointer fill-primary stroke-white" />
                          </div>
                        )}
                      </h3>
                      <p className="text-sm font-semibold text-muted-foreground">
                        {candidates?.job_title}
                      </p>
                      <p className="text-sm italic text-muted-foreground">{candidates?.email}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ) : (
              //  Show not found message
              <div className="mt-10 text-center text-xl text-muted-foreground">
                {t("send_request_without_job_post.candidate.user_not_found")}
              </div>
            )}
          </div>
          <div className="hidden flex-col md:flex md:items-end">
            <p className="font-medium text-muted-foreground">
              {t("send_request_without_job_post.candidate.not_found")}
            </p>
            <Input
              type="text"
              placeholder={t("send_request_without_job_post.candidate.enter_email")}
              className="mt-2 font-normal placeholder:text-muted-foreground"
              value={emailValue}
              onChange={(e) => {
                setEmailValue(e.target.value);
                trackEvent("Adding email for invite Candidate", { query: e.target.value });
              }}
            />
          </div>
        </div>

        <div className="absolute bottom-0 right-0 mt-8 flex w-full flex-col justify-between gap-2 bg-muted  p-4 shadow-lg md:right-5 md:w-1/4 md:rounded-tl-xl">
          <div className="flex flex-col md:hidden ">
            {candidates === null && (
              <p className="text-muted-foreground">
                {t("send_request_without_job_post.candidate.not_found")}
              </p>
            )}
            <Input
              type="text"
              placeholder={t("send_request_without_job_post.candidate.enter_email")}
              className="mt-2"
              value={emailValue}
              onChange={(e) => {
                setEmailValue(e.target.value);
                trackEvent("Adding email for invite Candidate", { query: e.target.value });
              }}
            />
          </div>
          {(selectedCandidates.length > 0 || emailValue.length > 0) && (
            <div className="flex items-start justify-between gap-4 text-sm font-semibold">
              <div className="flex gap-2">
                <ShoppingCart className="mr-2 mt-1 h-5 w-5" strokeWidth={3} />
                <div className="flex flex-col text-muted-foreground">
                  <div className="flex whitespace-nowrap">
                    <span className="whitespace-pre-wrap">{selectedPackage?.name}</span>
                    <span className="ml-1">
                      x {emailValue.length > 0 ? 1 : selectedCandidates.length}
                    </span>
                  </div>
                  <span>
                    ${selectedPackage?.price ?? 0} x{" "}
                    {emailValue.length > 0 ? 1 : selectedCandidates.length}
                  </span>
                </div>
              </div>

              <div className="text-lg font-semibold text-black/75">
                $
                {(
                  (selectedPackage?.price ?? 0) *
                  (emailValue.length > 0 ? 1 : selectedCandidates.length)
                ).toFixed(2)}
              </div>
            </div>
          )}
          {emailValue.length ? (
            <Button
              variant="default"
              className="w-full md:my-4 md:w-full"
              disabled={!emailValue.length}
              onClick={handleInvite}
            >
              {t("invite")}
            </Button>
          ) : (
            <Button
              variant="default"
              className="w-full md:my-4 md:w-full"
              disabled={!selectedCandidates.length}
              onClick={handleProceed}
            >
              {t("proceed")}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
